import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMe } from './store/slices/authSlice';
// Navbar is now integrated into MainLayout
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import VerifyOtp from './components/auth/VerifyOtp';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/layout/Dashboard';
import ProtectedRoute from './utils/ProtectedRoute';
import { Toaster } from 'react-hot-toast';
import './App.css';

// Admin components
import StudentManagement from './components/admin/StudentManagement';
import FacultyManagement from './components/admin/FacultyManagement';
import TeachingPointsView from './components/admin/TeachingPointsView';

// Super Instructor components
import CourseManagement from './components/superInstructor/CourseManagement';
import CourseStructureManager from './components/superInstructor/CourseStructureManager';
import CreateAssignment from './components/superInstructor/CreateAssignment';

// Instructor components
import TeachingPointsUpdate from './components/instructor/TeachingPointsUpdate';
import AssignmentReview from './components/instructor/AssignmentReview';
import InstructorCourses from './components/instructor/InstructorCourses';
import InstructorCourseDashboard from './components/instructor/InstructorCourseDashboard';

// Student components
import CourseView from './components/student/CourseView';
import CourseDashboard from './components/student/CourseDashboard';
import CourseDetail from './components/student/CourseDetail';
import AssignmentView from './components/student/AssignmentView';
import StudentProfile from './components/student/StudentProfile';
import StudentPerformance from './components/student/performance/StudentPerformance';

// Additional Performance Components
import InstructorPerformance from './components/instructor/performance/InstructorPerformance';
import SuperInstructorPerformance from './components/superInstructor/performance/SuperInstructorPerformance';
import PerformanceDetail from './components/common/performance/PerformanceDetail';

const App = () => {
  const dispatch = useDispatch();
  const { token, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (token) {
      dispatch(getMe());
    }
  }, [token, dispatch]);

  return (
    <Router>
      <div className="App">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'transparent',
              boxShadow: 'none',
              border: 'none',
              padding: 0
            }
          }} 
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {/* Admin Routes */}
          <Route
            path="/admin/students"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StudentManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/faculty"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <FacultyManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/teaching-points"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <TeachingPointsView />
              </ProtectedRoute>
            }
          />
          {/* Super Instructor Routes */}
          <Route
            path="/super-instructor/courses"
            element={
              <ProtectedRoute allowedRoles={['super_instructor']}>
                <CourseManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-instructor/courses/:courseId/structure"
            element={
              <ProtectedRoute allowedRoles={['super_instructor']}>
                <CourseStructureManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-instructor/create-assignment"
            element={
              <ProtectedRoute allowedRoles={['super_instructor']}>
                <CreateAssignment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-instructor/performance"
            element={
              <ProtectedRoute allowedRoles={['super_instructor']}>
                <SuperInstructorPerformance />
              </ProtectedRoute>
            }
          />
          {/* Instructor Routes */}
          <Route
            path="/instructor/teaching-points"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'super_instructor']}>
                <TeachingPointsUpdate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/assignments"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'super_instructor']}>
                <AssignmentReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'super_instructor']}>
                <InstructorCourses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/courses/:courseId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'super_instructor']}>
                <InstructorCourseDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/performance"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'super_instructor']}>
                <InstructorPerformance />
              </ProtectedRoute>
            }
          />
          {/* Student Routes */}
          <Route
            path="/student/courses"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <CourseView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/courses/:courseId"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <CourseDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/courses/:courseId/old"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <CourseDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/assignments"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <AssignmentView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/profile"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/performance"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <StudentPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/performance/self-eval/:courseId"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <PerformanceDetail mode="edit" type="self" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/performance/instructor-eval/:courseId"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <PerformanceDetail mode="view" type="instructor" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/performance/view-self/:studentId/:courseId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'super_instructor']}>
                <PerformanceDetail mode="view" type="self" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/performance/assess/:studentId/:courseId"
            element={
              <ProtectedRoute allowedRoles={['instructor', 'super_instructor']}>
                <PerformanceDetail mode="edit" type="instructor" />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
